import { formatNumber } from './format';

export class FileUtil {
	public static readonly archiveFileExtensions = ['zip', 'rar', '7z'];
	public static readonly documentFileExtensions = ['doc', 'docx', 'txt', 'pdf'];
	public static readonly fileSizeUnits = [
		' Bytes', 'KB', 'MB', 'GB', 'TB'
	];
	public static readonly fontFileExtensions = ['woff'];
	public static readonly imageFileExtensions = ['jpg', 'jpeg', 'gif', 'png'];
	public static readonly imageFileExtensionsAccept = FileUtil.imageFileExtensions.map(ext => `.${ext}`).join(',');
	public static readonly jpgPngFileExtensions = ['jpg', 'jpeg', 'png'];
	public static readonly jpgPngAccept = FileUtil.jpgPngFileExtensions.map(ext => `.${ext}`).join(',');
	public static readonly licenseFileExtensions = ['lic'];
	public static readonly Kb = 1000;
	public static readonly Mb = FileUtil.Kb * 1000;
	public static readonly Gb = FileUtil.Mb * 1000;
	public static readonly presentationFileExtensions = ['ppt', 'pptx'];
	public static readonly spreadsheetFileExtensions = ['xls', 'xlsx', 'csv'];
	public static readonly transcriptionFileExtensions = ['srt', 'vtt'];
	public static readonly videoFileExtensions = ['avi', 'm4v', 'f4v', 'flv', 'mpg', 'ts', 'mp4', 'avi', 'wmv', 'asf', 'mov', 'mkv', 'mts', 'mp3', 'ogv', 'm4a'];

	public static isImageFile(extension: string): boolean {
		return FileUtil.imageFileExtensions.includes(extension);
	}

	public static isJpgPngFile(extension: string): boolean {
		return FileUtil.jpgPngFileExtensions.includes(extension);
	}

	public static isVideoFile(extension: string): boolean {
		return FileUtil.videoFileExtensions.includes(extension);
	}

	public static isZipFile(extension: string): boolean {
		return 'zip' === extension;
	}

	public static isPresentationFile(extension: string): boolean {
		return FileUtil.presentationFileExtensions.includes(extension);
	}

	public static isDocumentFile(extension: string): boolean {
		return FileUtil.documentFileExtensions.includes(extension);
	}

	public static isCsvFile(extension: string): boolean {
		return extension === 'csv';
	}

	public static isSpreadsheetFile(extension: string): boolean {
		return FileUtil.spreadsheetFileExtensions.includes(extension);
	}

	public static isArchiveFile(extension: string): boolean {
		return FileUtil.archiveFileExtensions.includes(extension);
	}

	public static isTranscriptionFile(extension: string): boolean {
		return FileUtil.transcriptionFileExtensions.includes(extension);
	}

	public static isLicenseFile(extension: string): boolean {
		return FileUtil.licenseFileExtensions.includes(extension);
	}

	public static parseFileName(fileName: string): any {
		const matches = /^(.*)\.([^.]*)$/.exec(fileName);
		if(matches) {
			return {
				name: matches[1],
				extension: matches[2].toLowerCase()
			};
		}
	}

	public static getFileExtension(fileName: string): string {
		const match = /\.([^.]+)$/.exec(fileName); // return 'pdf' from 'document.pdf'
		return match ? match[1] : '';
	}

	/**
	 * Formats a file size
	 * @param size - Size of the file in bytes
	 * @param sizeOutputUnit - If null, a unit will be chosen automatically based on size
	 */
	public static formatFileSize(size: number, sizeOutputUnit?: string): string {
		if (!size || !isFinite(size)) {
			return size as any;
		}

		const unitOut = sizeOutputUnit
			? FileUtil.fileSizeUnits.indexOf(sizeOutputUnit)
			: FileUtil.findTargetUnit(size);

		const scaledSize = size / Math.pow(FileUtil.Kb, unitOut);

		return formatNumber(scaledSize, '1.2-2') + FileUtil.fileSizeUnits[unitOut];
	}

	public static getFileExtensionCssClass(fileExtension: string): string {
		if(FileUtil.isImageFile(fileExtension)) {
			return 'picture';
		}
		return fileExtension ? `filetype ${fileExtension}` : '';
	}

	private static findTargetUnit(sizeBytes: number){
		const heuristicSize = Math.abs(sizeBytes) || 1;
		const unit = Math.floor(Math.log(heuristicSize) / Math.log(FileUtil.Kb));
		const maxAllowedUnit = FileUtil.fileSizeUnits.length - 1;
		return Math.min(unit, maxAllowedUnit);
	}
}
